import { humanize } from '../../../../lib/formatLabels';
import { useQuery } from '@tanstack/react-query';
import { SchoolApi } from '../../../../api/client';
import { useClassPerfOverview } from '../../../../api/hooks';
import { EmptyState, StatTile, StatGrid, Term } from '../../../../components/ui';
import Spinner from '../../../../components/ui/Spinner';
import ErrorPanel from '../../../../components/ui/ErrorPanel';
import { FaShieldAlt } from '../../../../components/ui/icons';
import TrackRows, { type TrackRowItem } from '../../components/TrackRows';

interface Props { gradeId: number; sectionId?: number | 'all'; }

/** Latest-state counts at or below this are drawn in the accent colour (design `fill lo`). */
const LOW_COUNT = 10;

export default function WellbeingTab({ gradeId, sectionId = 'all' }: Props) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['analytics.wellbeing', gradeId, sectionId],
    queryFn: () => SchoolApi.analyticsWellbeing({
      grade: gradeId,
      ...(sectionId !== 'all' ? { section: sectionId } : {}),
    }),
  });
  // Cohort size for "of N in cohort"; the same query the Overview tab and page subtitle use.
  const cohort = useClassPerfOverview({ grade: gradeId, section: sectionId, subject: 'all' }).data?.cohort_size;

  if (isLoading) return <div className="flex justify-center py-8"><Spinner size="md" /></div>;
  if (isError || !data) return <ErrorPanel />;

  const dotTotal = data.dot_counts.ok + data.dot_counts.watch + data.dot_counts.concern;
  if (dotTotal === 0) {
    return (
      <EmptyState
        icon={<FaShieldAlt />}
        title="No wellbeing data yet for this grade"
        body="Students need to opt in via the mobile app first."
      />
    );
  }

  const stateEntries = Object.entries(data.latest_state_counts).map(([state, count]) => ({
    state: state || '(none)',
    count,
  }));
  const stateTotal = stateEntries.reduce((n, e) => n + e.count, 0);
  const maxStateCount = Math.max(1, ...stateEntries.map((e) => e.count));
  const stateRows: TrackRowItem[] = stateEntries.map((e) => ({
    key: e.state,
    label: humanize(e.state),
    count: e.count,
    // Bars are scaled to the most common state (design `.hb` widths), not to the total.
    pct: (e.count / maxStateCount) * 100,
    tone: e.count <= LOW_COUNT ? 'accent' as const : 'slate' as const,
    ariaLabel: `${humanize(e.state)}: ${e.count} of ${stateTotal} students`,
  }));

  const split = [
    { key: 'ok', label: 'OK', count: data.dot_counts.ok, swatch: 'bg-slate' },
    { key: 'watch', label: 'Watch', count: data.dot_counts.watch, swatch: 'bg-meta-6' },
    { key: 'concern', label: 'Concern', count: data.dot_counts.concern, swatch: 'bg-accent' },
  ];

  return (
    <>
      <StatGrid>
        <StatTile label="Open wellbeing cases" value={data.open_cases_count} help="open_cases" />
        <StatTile label="Escalated cases" value={<span className="text-danger-ink">{data.escalated_cases_count}</span>} help="escalated_cases" tone="accent" />
        <StatTile label="Students opted in" value={dotTotal} help="opted_in"
          sub={cohort != null ? `of ${cohort} in cohort` : undefined} />
        <StatTile label="Flagged as concern" value={data.dot_counts.concern} help="concern_count" tone="accent" />
      </StatGrid>
      <div className="mt-[22px] grid gap-[18px] min-[821px]:grid-cols-2">
        <div className="min-w-0 rounded-[16px] bg-panel p-[18px]">
          <h3 className="font-display text-[15px] font-bold tracking-[-0.01em] text-ink"><Term k="wellbeing_dot">Wellbeing status</Term></h3>
          <div aria-hidden className="mb-3 mt-4 flex h-4 gap-[3px] overflow-hidden rounded-[8px]">
            {split.map((r) => <span key={r.key} className={r.swatch} style={{ flex: r.count }} />)}
          </div>
          <ul>
            {split.map((r) => (
              <li key={r.key} className="flex items-center justify-between border-b border-hairline py-3 last:border-b-0">
                <span className="flex items-center gap-2"><span aria-hidden className={`inline-block h-2.5 w-2.5 rounded-[3px] ${r.swatch}`} />{r.label}</span>
                <span className="font-mono font-semibold tabular-nums" aria-label={`${r.label}: ${r.count} of ${dotTotal} students`}>{r.count}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="min-w-0 rounded-[16px] bg-panel p-[18px]">
          <h3 className="mb-2.5 font-display text-[15px] font-bold tracking-[-0.01em] text-ink"><Term k="latest_state">Latest reported state</Term></h3>
          {stateRows.length > 0
            ? <TrackRows rows={stateRows} />
            : <p className="text-sm text-muted">No state data.</p>}
        </div>
      </div>
    </>
  );
}
